import { Component, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { CustomValidators } from '../../../../validators/custom-validators';
import { RoleService } from '../../../../services/role/role-service';
import { RoleDTO, RolesDTO } from '../../../../pages/admin-dashboard/user-management/user-management.model';
import { AuthService } from '../../../../services/auth-service';
import { CreateUserByAdminDTO } from '../../../../services/auth-service.model';
import { SuccessfulCreationModal } from './successful-creation-modal/successful-creation-modal';
import { finalize } from 'rxjs';
import { Error } from '../../../public-user-component/auth/shared/error/error';

@Component({
  selector: 'app-add-user-form',
  imports: [ReactiveFormsModule, SuccessfulCreationModal, Error],
  templateUrl: './add-user-form.html',
  styleUrl: './add-user-form.scss',
})
export class AddUserForm implements OnInit {
  private router = inject(Router);
  private roleService = inject(RoleService);
  private locale = inject(LOCALE_ID);
  private userMgtService = inject(AuthService);
  formSubmitted = false;
  showPasswordFlag = false;
  roles = signal<RoleDTO[]>([]);
  rolesTitle = signal<{ id: number; title: string }[]>([]);
  isRoleMenu = signal(false);
  createdUser = signal<{ firstName: string; lastName: string; username: string; password: string }>({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
  });
  isSuccessModalOpen = signal(false);
  loading = signal(false);
  error = signal<string>('');

  userForm = new FormGroup({
    firstName: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),

      Validators.pattern(/^(?! )[A-Za-z\u0600-\u06FF]+( [A-Za-z\u0600-\u06FF]+)*$/),
    ]),
    lastName: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(50),
      Validators.pattern(/^(?! )[A-Za-z\u0600-\u06FF]+( [A-Za-z\u0600-\u06FF]+)*$/),
    ]),

    phoneNumber: new FormControl('', [Validators.required, CustomValidators.adminUserMobileNumber()]),
    email: new FormControl('', [
      // Validators.required,
      Validators.minLength(5),
      Validators.maxLength(150),
      // CustomValidators.email(),
      Validators.pattern(/^(?!.*@xn--)[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)+$/),
    ]),

    role: new FormControl<number[]>([], [Validators.required]),
  });
  onRoleSelected(event: Event, roleId: number) {
    const control = this.userForm.get('role');
    const checked = (event.target as HTMLInputElement).checked;

    if (!control) return;

    const currentValue = (control.value as number[]) || [];
    if (checked) {
      if (!currentValue.includes(roleId)) {
        currentValue.push(roleId);
      }
    } else {
      const index = currentValue.indexOf(roleId);
      if (index > -1) {
        currentValue.splice(index, 1);
      }
    }

    control.setValue(currentValue);
    control.markAsTouched();
  }
  openModal() {
    this.isSuccessModalOpen.set(true);
  }
  closeModal() {
    this.isSuccessModalOpen.set(false);
  }

  onPhoneInput(event: Event) {
    const target = event.target as HTMLInputElement;
    let digits = target.value.replace(/\D/g, '');

    // trim leading 0 if user enters 11 digits starting with 0
    if (digits.startsWith('0') && digits.length === 11) {
      digits = digits.slice(1);
    }

    target.value = digits;
    this.userForm.get('phoneNumber')!.setValue(digits, { emitEvent: false });
  }
  onSubmit() {
    this.error.set('');
    this.formSubmitted = true;
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }
    this.loading.set(true);

    if (this.userForm.valid) {
      const mobileControlValue = this.userForm.value.phoneNumber;

      let mobileNumber = '';
      if (mobileControlValue?.startsWith('0') && mobileControlValue?.length === 11) {
        mobileNumber = '+20' + mobileControlValue.slice(1);
      }
      if (!mobileControlValue?.startsWith('0') && mobileControlValue?.length === 10) {
        mobileNumber = '+20' + mobileControlValue;
      }
      const { email, role, firstName, lastName } = this.userForm.value!;
      const formData: CreateUserByAdminDTO = {
        firstName: firstName!,
        lastName: lastName!,
        mobileNumber,
        roleIds: role!,
        ...(email && { email }), // only include email if it has a value
      };
      // console.log(formData);
      // return;

      this.userMgtService
        .createUserByAdmin(formData as CreateUserByAdminDTO)
        .pipe(
          finalize(() => {
            this.loading.set(false);
          }),
        )
        .subscribe({
          next: (response) => {
            // console.log('User created successfully:', response);
            this.openModal();
            this.createdUser.set({
              username: formData.mobileNumber,
              password: response.password,
              firstName: formData.firstName,
              lastName: formData.lastName,
            });
          },
          error: (error) => {
            this.isRoleMenu.set(false);
            if (this.locale.startsWith('ar')) {
              this.error.set(error.messageAr || 'حدث خطأ أثناء إنشاء المستخدم');
            }
            if (this.locale.startsWith('en')) {
              this.error.set(error.messageEn || 'An error occurred while creating the user');
            }
          },
        });
    } else {
      // console.log('Form is invalid');
      this.userForm.markAllAsTouched();
    }
  }

  onCancel() {
    this.router.navigate(['/admin/user-management/']);
  }

  togglePasswordVisibility() {
    this.showPasswordFlag = !this.showPasswordFlag;
  }

  getTranslatedRolesTitle(fetchedRoles: RoleDTO[]) {
    if (this.locale.startsWith('ar')) {
      console.log('it should display roles:', this.roles());
      console.log('it should display roles title:', this.rolesTitle());
      this.rolesTitle.set(fetchedRoles.map((role) => ({ id: role.id, title: role.titleAr })));
    }
    if (this.locale.startsWith('en')) {
      this.rolesTitle.set(fetchedRoles.map((role) => ({ id: role.id, title: role.titleEn })));
    }
  }

  getRoles() {
    this.roleService.getAllRoles().subscribe({
      next: (res: RolesDTO) => {
        this.getTranslatedRolesTitle(res.list);
      },
      error: () => {},
    });
  }
  ngOnInit() {
    this.getRoles();
  }

  onToggleRoleMenu() {
    this.isRoleMenu.set(!this.isRoleMenu());
  }
}
